export interface ActivityEntry {
  ts: number;
  tool: string;
  path: string;
  cmd: string;
}

interface DispatchIntent {
  dwellerId: string;
  targetRoom: string;
}

/**
 * Maps a Claude Code tool call entry to the dweller most likely performing that work.
 * Returns null if the entry should be ignored (no matching dweller).
 */
export function mapActivityToDweller(entry: ActivityEntry): DispatchIntent | null {
  const tool = entry.tool.toLowerCase();
  const path = entry.path.toLowerCase();
  const cmd  = entry.cmd.toLowerCase();

  // Memory write → The Scribe
  if (tool === 'write' && (path.includes('memory') || path.includes('decision') || path.includes('log'))) {
    return { dwellerId: 'scribe', targetRoom: 'Archive Vault' };
  }

  // Web research → The Scout
  if (tool === 'websearch' || tool === 'webfetch' || tool === 'browse') {
    return { dwellerId: 'scout', targetRoom: 'Comm Room' };
  }

  // Analytics / metrics → The Analyst
  if (
    tool === 'bash' &&
    (cmd.includes('metric') || cmd.includes('analytic') || cmd.includes('cohort') || cmd.includes('retention'))
  ) {
    return { dwellerId: 'analyst', targetRoom: 'Data Center' };
  }

  // Interview / survey reads → The Investigator
  if (
    (tool === 'read' || tool === 'websearch') &&
    (path.includes('survey') || path.includes('interview') || path.includes('user') || path.includes('customer'))
  ) {
    return { dwellerId: 'investigator', targetRoom: 'Survey Room' };
  }

  // Any code write → The Tinkerer (Phase 3 dweller, skip for now)
  // Generic tool use → The Overseer routes the plan
  if (tool === 'write' || tool === 'edit' || tool === 'bash') {
    return { dwellerId: 'overseer', targetRoom: "Overseer's Office" };
  }

  return null;
}

/** Parse a raw JSON line from the activity log. Returns null on parse error. */
export function parseActivityLine(line: string): ActivityEntry | null {
  try {
    const obj = JSON.parse(line) as Partial<ActivityEntry>;
    if (typeof obj.ts !== 'number' || typeof obj.tool !== 'string') return null;
    return {
      ts: obj.ts,
      tool: obj.tool ?? '',
      path: obj.path ?? '',
      cmd: obj.cmd ?? '',
    };
  } catch {
    return null;
  }
}
